import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { PhoneDevicesByFilterQueryVariables, PhoneDeviceStatus, usePhoneDevicesByFilterQuery } from "../../graphql"
import { PhoneDevicesList } from "./PhoneDevicesList"

export type PhoneDevicesByFilterProps = {
	keyword?: string
	status?: PhoneDeviceStatus
}

export const PhoneDevicesByFilter: React.FC<PhoneDevicesByFilterProps> = ({ keyword, status }) => {
	const [filter, setFilter] = useState({ keyword, status })

	const [pagination, setPagination] = useState<PhoneDevicesByFilterQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = usePhoneDevicesByFilterQuery({
		variables: { filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, status })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, status])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.phoneDevicesByFilter.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.phoneDevicesByFilter ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading phone devices</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.phoneDevicesByFilter.phoneDevices && data.phoneDevicesByFilter.phoneDevices.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.phoneDevicesByFilter.phoneDevices.length || 0} phone device
						{(data.phoneDevicesByFilter.phoneDevices.length || 0) > 1 ? "s" : ""}
					</Text>

					<PhoneDevicesList phoneDevices={data.phoneDevicesByFilter.phoneDevices} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more phone devices</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any phone devices.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
